import type { Agent } from '@/types/agent-types';

export interface AgentFormSnapshot {
  name: string;
  description: string;
  modelName: string;
  modelProvider: string;
  systemPrompt: string;
  maxTokens: number;
  temperature: number;
  topP: number;
  thinkingEnabled: boolean;
  thinkingBudgetTokens: number;
  enableWorkflowRuntime: boolean;
  enableWorkflowAuthoring: boolean;
  enableA2AOutbound: boolean;
  enableScheduling: boolean;
  exposedViaA2A: boolean;
  renderFilesInChat: boolean;
  showIntermediateFiles: boolean;
  allowUserWorkflows: boolean;
  enableGithubTools: boolean;
  memoryEnabled: boolean;
  memoryRetentionDays: number;
  enableTaskAlignmentGuardrail: boolean;
  taskAlignmentMinRelevance: number;
  e2bEnabled: boolean;
  e2bTemplate: string;
  e2bRunTimeoutS: number;
  e2bMaxEditCalls: number;
  e2bAllowInternet: boolean;
  e2bSandboxEnv: { key: string; value: string }[];
  refuseSyntheticDataGeneration: boolean;
  workflowAllowlist: string[];
  workflowAllowlistLabels: string[];
  mcpToolAllowlist: string[];
  dataCollectionAllowlist: string[];
}

export function snapshotFromAgent(agent: Agent): AgentFormSnapshot {
  return {
    name: agent.name,
    description: agent.description,
    modelName: agent.model_name,
    modelProvider: agent.model_provider,
    systemPrompt: agent.system_prompt,
    maxTokens: agent.max_tokens,
    temperature: agent.temperature,
    topP: agent.top_p ?? 1.0,
    thinkingEnabled: agent.thinking_enabled ?? false,
    thinkingBudgetTokens: agent.thinking_budget_tokens ?? 8_192,
    enableWorkflowRuntime: agent.enable_workflow_runtime,
    enableWorkflowAuthoring: agent.enable_workflow_authoring,
    enableA2AOutbound: agent.enable_a2a_outbound,
    enableScheduling: agent.enable_scheduling ?? true,
    exposedViaA2A: agent.exposed_via_a2a,
    renderFilesInChat: agent.render_files_in_chat ?? true,
    showIntermediateFiles: agent.show_intermediate_files ?? false,
    allowUserWorkflows: agent.allow_user_workflows ?? false,
    enableGithubTools: agent.enable_github_tools ?? false,
    memoryEnabled: agent.memory_enabled ?? false,
    memoryRetentionDays: agent.memory_retention_days ?? 0,
    enableTaskAlignmentGuardrail:
      agent.enable_task_alignment_guardrail ?? false,
    taskAlignmentMinRelevance: agent.task_alignment_min_relevance ?? 40,
    e2bEnabled: agent.e2b_enabled ?? true,
    e2bTemplate: agent.e2b_template ?? '',
    e2bRunTimeoutS: agent.e2b_run_timeout_s ?? 0,
    e2bMaxEditCalls: agent.e2b_max_edit_calls_per_turn ?? 0,
    e2bAllowInternet: agent.e2b_allow_internet_access ?? true,
    e2bSandboxEnv: Object.entries(agent.e2b_sandbox_env ?? {}).map(
      ([key, value]) => ({ key, value }),
    ),
    refuseSyntheticDataGeneration:
      agent.allow_synthetic_data_generation !== true,
    workflowAllowlist: agent.workflow_allowlist,
    workflowAllowlistLabels: agent.workflow_allowlist_labels,
    mcpToolAllowlist: agent.mcp_tool_allowlist,
    dataCollectionAllowlist: agent.data_collection_allowlist,
  };
}

export const INITIAL_FORM: AgentFormSnapshot = {
  name: '',
  description: '',
  modelName: 'claude-opus-4-8-fast',
  modelProvider: 'anthropic',
  systemPrompt: 'You are a helpful assistant.',
  maxTokens: 32_000,
  temperature: 0.7,
  topP: 1.0,
  thinkingEnabled: false,
  thinkingBudgetTokens: 8_192,
  enableWorkflowRuntime: true,
  enableWorkflowAuthoring: true,
  enableA2AOutbound: true,
  enableScheduling: true,
  exposedViaA2A: true,
  renderFilesInChat: true,
  showIntermediateFiles: false,
  allowUserWorkflows: false,
  enableGithubTools: false,
  memoryEnabled: false,
  memoryRetentionDays: 0,
  enableTaskAlignmentGuardrail: false,
  taskAlignmentMinRelevance: 40,
  e2bEnabled: true,
  e2bTemplate: '',
  e2bRunTimeoutS: 0,
  e2bMaxEditCalls: 0,
  e2bAllowInternet: true,
  e2bSandboxEnv: [],
  refuseSyntheticDataGeneration: true,
  workflowAllowlist: [],
  workflowAllowlistLabels: [],
  mcpToolAllowlist: [],
  dataCollectionAllowlist: [],
};

// Allowlists and sandbox env are order-independent sets, so a reordered row
// must not read as a change — sort arrays before comparing.
function normalizeForCompare(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value
      .map(normalizeForCompare)
      .sort((a, b) => (JSON.stringify(a) < JSON.stringify(b) ? -1 : 1));
  }
  return value;
}

export function countChangedFields(a: AgentFormSnapshot, b: AgentFormSnapshot) {
  return (Object.keys(a) as (keyof AgentFormSnapshot)[]).filter(
    (key) =>
      JSON.stringify(normalizeForCompare(a[key])) !==
      JSON.stringify(normalizeForCompare(b[key])),
  ).length;
}

// The persisted shape after save: trimmed name/template and sandbox-env rows
// with blank keys dropped and keys trimmed — matches what the save payload
// sends, so the dirty baseline reflects server state rather than raw input.
export function cleanedFormSnapshot(s: AgentFormSnapshot): AgentFormSnapshot {
  return {
    ...s,
    name: s.name.trim(),
    e2bTemplate: s.e2bTemplate.trim(),
    e2bSandboxEnv: s.e2bSandboxEnv
      .filter((entry) => entry.key.trim())
      .map((entry) => ({ key: entry.key.trim(), value: entry.value })),
  };
}
